/**
 * Sidebar Component
 *
 * Vertical icon sidebar for main actions.
 * Visually extends from the header logo area.
 */

import { useState } from 'react';
import { PlusCircle, Flag, ChatCircle, Info, FunnelSimple } from '@phosphor-icons/react';
import { COLORS } from '../../lib/constants';

interface SidebarProps {
  onSubmitClick?: () => void;
  onClaimClick?: () => void;
  onFeedbackClick?: () => void;
  onInfoClick?: () => void;
  onFilterToggle?: () => void;
  filtersVisible?: boolean;
}

const styles = {
  sidebar: {
    position: 'absolute' as const,
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0px',
    padding: '2px',
    backgroundColor: `${COLORS.parchment}ee`,
    backdropFilter: 'blur(12px)',
    borderRadius: 0,
    boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
    zIndex: 20,
  },
  iconButton: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '52px',
    height: '52px',
    borderRadius: '0',
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.graphite,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  iconButtonHover: {
    backgroundColor: `${COLORS.graphite}10`,
    color: COLORS.royalBlue,
  },
  iconButtonActive: {
    backgroundColor: COLORS.royalBlue,
    color: '#fff',
  },
  tooltip: {
    position: 'absolute' as const,
    left: '60px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '6px 10px',
    backgroundColor: COLORS.graphite,
    color: COLORS.parchment,
    fontSize: '12px',
    borderRadius: '4px',
    whiteSpace: 'nowrap' as const,
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden' as const,
    transition: 'opacity 0.15s ease, visibility 0.15s ease',
  },
  tooltipVisible: {
    opacity: 1,
    visibility: 'visible' as const,
  },
  tooltipArrow: {
    position: 'absolute' as const,
    left: '-4px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '0',
    height: '0',
    borderTop: '4px solid transparent',
    borderBottom: '4px solid transparent',
    borderRight: `4px solid ${COLORS.graphite}`,
  },
};

interface IconButtonProps {
  icon: React.ReactNode;
  tooltip: string;
  onClick?: () => void;
  ariaLabel: string;
  isActive?: boolean;
}

function IconButton({ icon, tooltip, onClick, ariaLabel, isActive }: IconButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      style={{
        ...styles.iconButton,
        ...(isActive ? styles.iconButtonActive : {}),
        ...(isHovered && !isActive ? styles.iconButtonHover : {}),
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={ariaLabel}
    >
      {icon}
      <span
        style={{
          ...styles.tooltip,
          ...(isHovered ? styles.tooltipVisible : {}),
        }}
      >
        <span style={styles.tooltipArrow} />
        {tooltip}
      </span>
    </button>
  );
}

export function Sidebar({
  onSubmitClick,
  onClaimClick,
  onFeedbackClick,
  onInfoClick,
  onFilterToggle,
  filtersVisible,
}: SidebarProps) {
  return (
    <aside style={styles.sidebar}>
      <IconButton
        icon={<Info size={40} weight="light" />}
        tooltip="Over deze kaart"
        onClick={onInfoClick}
        ariaLabel="About this map"
      />
      <IconButton
        icon={<PlusCircle size={40} weight="light" />}
        tooltip="Organisatie toevoegen"
        onClick={onSubmitClick}
        ariaLabel="Submit organization"
      />
      <IconButton
        icon={<Flag size={40} weight="light" />}
        tooltip="Organisatie claimen"
        onClick={onClaimClick}
        ariaLabel="Claim organization"
      />
      <IconButton
        icon={<ChatCircle size={40} weight="light" />}
        tooltip="Feedback geven"
        onClick={onFeedbackClick}
        ariaLabel="Send feedback"
      />
      <IconButton
        icon={<FunnelSimple size={40} weight="light" />}
        tooltip={filtersVisible ? 'Filters verbergen' : 'Filters tonen'}
        onClick={onFilterToggle}
        ariaLabel="Toggle filters"
        isActive={filtersVisible}
      />
    </aside>
  );
}
